import React from 'react';
import Link from 'next/link';
import { Hit, SearchResponse } from '@algolia/client-search';
import { Table, DotIcon, MorningIcon, AfternoonIcon } from '@spa-cars/ui';
import { useLazyQuery } from '@apollo/client';
import { validateString } from 'avilatek-utils';
import { Location, PaginationInfo, User } from '@spa-cars/models';
import { useAlgolia } from '../../hooks';
import { GET_USERS_LIST } from '../../graphql/queries';

interface PaginationDataProps {
  items: Array<User>;
  count: number;
  pageInfo: PaginationInfo;
}

const getShift = (shift: string) => {
  switch (shift) {
    case 'afternoon':
      return 'Tarde';
    case 'morning':
      return 'Mañana';

    default:
      return 'Sin turno';
  }
};

const buildTableData = (users: Array<User | Hit<User>> = []) =>
  users.map((user) => ({
    _id: user?._id ?? (user as any)?.id,
    name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}` ?? 'N/A',
    email: user?.email?.toLowerCase() ?? 'N/A',
    location: (user.location as unknown as Location)?.name ?? '',
    shift: (
      <p className="flex gap-[10px] items-center">
        {user.shift === 'afternoon' ? (
          <AfternoonIcon className="w-5 h-5" />
        ) : null}
        {user.shift === 'morning' ? (
          <MorningIcon className="w-5 h-5 text-[#E2C200]" />
        ) : null}
        {getShift(user.shift)}
      </p>
    ),
    status: (
      <p className="flex gap-[10px] items-center">
        <DotIcon
          className={` ${
            user?.active ? 'text-primary-300' : ' text-danger-200'
          } w-[15px] h-[15px]`}
        />
        {user?.active ? 'Activo' : 'Inactivo'}
      </p>
    ),
  }));

interface OperatorTableProps {
  locationId: string;
  locationName: string;
}

function OperatorTable({ locationId, locationName }: OperatorTableProps) {
  const searchUser = useAlgolia();
  const searchIndex = searchUser.initIndex('users');
  const [searchResult, setSearchResult] = React.useState<Hit<User>[]>(null);
  const [paginationData, setPaginationData] = React.useState<
    PaginationDataProps | SearchResponse<User>
  >(null);
  const [perPage, setPerPage] = React.useState(10);
  const [searchInput, setSearchInput] = React.useState(''); // guarda lo que el usuario ingresa en el input de busqueda
  const [loadingSearch, setLoadingSearch] = React.useState(false);
  const [loadOperators, { data, loading, error, fetchMore }] = useLazyQuery<{
    userPagination: {
      items: Array<User>;
      count: number;
      pageInfo: PaginationInfo;
    };
  }>(GET_USERS_LIST);

  const queryVariables = {
    perPage: perPage ?? 10,
    sort: 'CREATEDAT_DESC',
    filter: {
      userType: 'mechanic',
      location: locationId,
    },
  };

  const handleGoToPage = (value) => {
    if (validateString(searchInput)) {
      searchOperators(searchInput, value - 1); // ir a la siguiente pagina de la busqueda de algolia
    } else {
      loadOperators({
        variables: {
          page: value ?? 1,
          ...queryVariables,
        },
        fetchPolicy: 'cache-and-network',
      }); // ir a la siguiente pagina del query
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <p className="flex justify-between items-center">
            NOMBRE
            {/* <MenuIcon className="w-5 h-5 text-neutral-100" /> */}
          </p>
        ),

        accessor: 'name',
      },
      {
        Header: (
          <p className="flex justify-between items-center">
            CORREO ELECTRÓNICO
            {/* <MenuIcon className="w-5 h-5 text-neutral-100" /> */}
          </p>
        ),
        accessor: 'email',
      },
      {
        Header: (
          <p className="flex justify-between items-center">
            PUNTO DE SERVICIO
            {/* <MenuIcon className="w-5 h-5 text-neutral-100" /> */}
          </p>
        ),
        accessor: 'location',
      },
      {
        Header: (
          <p className="flex justify-between items-center">
            TURNO
            {/* <MenuIcon className="w-5 h-5 text-neutral-100" /> */}
          </p>
        ),
        accessor: 'shift',
      },
      {
        Header: (
          <p className="flex justify-between items-center">
            ESTATUS
            {/* <MenuIcon className="w-5 h-5 text-neutral-100" /> */}
          </p>
        ),
        accessor: 'status',
      },
    ],
    []
  );

  const filterResults = React.useCallback(
    (results: SearchResponse<User>) => results?.hits ?? [],
    []
  );

  const searchOperators = async (value: string, page_: number) => {
    try {
      if (!validateString(value) || value === '') {
        loadOperators({
          variables: {
            page: 1,
            ...queryVariables,
          },
          fetchPolicy: 'cache-and-network',
        });
        setPaginationData(data?.userPagination);
        setSearchResult([]);
        return;
      }
      setLoadingSearch(true);
      const result = await searchIndex.search<User>(value, {
        filters: `location.id:${locationId}`,
        facetFilters: [`userType:mechanic`],
        page: page_,
        hitsPerPage: perPage ?? 10,
      });
      setLoadingSearch(false);
      setPaginationData(result);
      const resultsFiltered = filterResults(result);
      setSearchResult(resultsFiltered);
    } catch (err) {
      console.log(err);
    }
  };

  const onChangeSearch = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      setSearchInput(e.target.value);
    },
    []
  );

  const dataMemo = React.useMemo(
    () =>
      searchResult?.length > 0
        ? buildTableData(searchResult)
        : buildTableData((paginationData as PaginationDataProps)?.items) ?? [],
    [paginationData, searchResult]
  );

  React.useEffect(() => {
    if (data) {
      setPaginationData(data?.userPagination);
    }
  }, [data]);

  React.useEffect(() => {
    searchOperators(searchInput, 0); // para algolia, la paginación empieza en 0
  }, [searchInput, perPage]);

  return (
    <Table
      perPage={perPage}
      setPerPage={setPerPage}
      columns={columns}
      data={dataMemo}
      error={null}
      loading={loading || loadingSearch}
      pageCount={0}
      header="Operadores"
      pageNumber={
        ((paginationData as SearchResponse<User>)?.page ??
          ((paginationData as PaginationDataProps)?.pageInfo?.currentPage ??
            1) - 1) + 1
      }
      addButtonText="Operador"
      handleGoToPage={handleGoToPage}
      results={
        (paginationData as SearchResponse<User>)?.nbHits ??
        (paginationData as PaginationDataProps)?.count
      }
      path="app/operators"
      link={Link}
      onChangeSearch={onChangeSearch}
    />
  );
}

export default OperatorTable;
